import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from '@mui/material';
import { ArrowLeft, ExternalLink, MoreVertical, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MarkdownRenderer } from '../../../components/common/MarkdownRenderer';
import { getNotification } from '../api/notifications.api';
import { useNotifications } from '../hooks/useNotifications';
import { CouponCard } from './CouponCard';
import type { NotificationDetail as NotificationDetailType } from '../types';

interface NotificationDetailProps {
  publicId: string;
  onBack?: () => void;
  onNotFound?: () => void;
}

export const NotificationDetail: React.FC<NotificationDetailProps> = ({
  publicId,
  onBack,
  onNotFound,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { markRead, markUnread, archive, refreshCount } = useNotifications();
  const [detail, setDetail] = useState<NotificationDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const markedReadRef = useRef<Set<string>>(new Set());
  const onNotFoundRef = useRef(onNotFound);

  useEffect(() => {
    onNotFoundRef.current = onNotFound;
  }, [onNotFound]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setDetail(null);

    getNotification(publicId)
      .then((data) => {
        if (!cancelled) {
          setDetail(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          onNotFoundRef.current?.();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [publicId]);

  useEffect(() => {
    if (!detail || detail.readAt || markedReadRef.current.has(detail.publicId)) {
      return;
    }

    markedReadRef.current.add(detail.publicId);
    void markRead(detail.publicId)
      .then(() => {
        setDetail((current) =>
          current?.publicId === detail.publicId
            ? { ...current, readAt: current.readAt ?? new Date().toISOString() }
            : current
        );
      })
      .catch(() => {
        markedReadRef.current.delete(detail.publicId);
      })
      .finally(() => void refreshCount());
  }, [detail, markRead, refreshCount]);

  const publishedAt = useMemo(() => {
    if (!detail) return '';
    return new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(detail.publishedAt));
  }, [detail, i18n.language]);

  const handleCta = () => {
    if (!detail?.cta) return;

    if (detail.cta.url.startsWith('/')) {
      navigate(detail.cta.url);
      return;
    }

    window.open(detail.cta.url, '_blank', 'noopener,noreferrer');
  };

  const handleMarkUnread = () => {
    if (!detail) return;
    setMenuAnchor(null);
    void markUnread(detail.publicId)
      .then(() => {
        setDetail({ ...detail, readAt: null });
      })
      .finally(() => void refreshCount());
  };

  const handleDelete = async () => {
    if (!detail) return;
    setDeleting(true);
    try {
      await archive(detail.publicId);
      setDeleteOpen(false);
      navigate('/notifications');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box className="space-y-4 p-5">
        <Skeleton width="70%" height={36} />
        <Skeleton width="35%" />
        <Skeleton height={120} />
        <Skeleton height={80} />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box className="flex min-h-[260px] items-center justify-center p-6 text-center">
        <Typography className="text-gray-500 dark:text-slate-400">
          {t('notifications.notFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="min-w-0 p-4 text-gray-900 dark:text-slate-100 md:p-6">
      <Box className="mb-4 flex items-start justify-between gap-3">
        <Box className="min-w-0">
          {onBack && (
            <Button
              size="small"
              startIcon={<ArrowLeft size={16} />}
              onClick={onBack}
              className="mb-3 normal-case"
            >
              {t('notifications.back')}
            </Button>
          )}
          <Typography variant="h5" className="mb-3 font-semibold">
            {detail.title}
          </Typography>
          <Box className="flex flex-wrap items-center gap-2">
            <Chip size="small" label={t(`notifications.category.${detail.category}`)} />
            <Chip
              size="small"
              color={detail.severity === 'CRITICAL' ? 'error' : 'warning'}
              variant={detail.severity === 'INFO' ? 'outlined' : 'filled'}
              label={t(`notifications.severity.${detail.severity}`)}
            />
            <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
              {publishedAt}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label={t('notifications.actions')}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        >
          <MoreVertical size={20} />
        </IconButton>
      </Box>

      <MarkdownRenderer
        text={detail.body}
        className="prose-sm max-w-none text-gray-800 dark:text-slate-200"
      />

      {detail.payload?.type === 'coupon' && <CouponCard payload={detail.payload} />}

      {detail.cta &&
        (detail.payload?.type !== 'coupon' ||
          !detail.payload.status ||
          detail.payload.status === 'UNUSED') && (
          <Button
            variant="contained"
            onClick={handleCta}
            endIcon={!detail.cta.url.startsWith('/') ? <ExternalLink size={16} /> : undefined}
            className="mt-6 normal-case"
          >
            {detail.cta.label}
          </Button>
        )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={handleMarkUnread}>{t('notifications.markUnread')}</MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setDeleteOpen(true);
          }}
          className="text-red-600"
        >
          <Trash2 size={16} className="mr-2" />
          {t('notifications.delete')}
        </MenuItem>
      </Menu>

      <Dialog open={deleteOpen} onClose={deleting ? undefined : () => setDeleteOpen(false)}>
        <DialogTitle>{t('notifications.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            {t('notifications.deleteConfirm.message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t('notifications.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
